import { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  useDeviceType,
} from '@packages/shared';
import { Select } from '@packages/shared/src/components/Select';
import { RadioGroup } from '@packages/shared/src/components/RadioGroup';
import { useCustomer, useConfig } from '@packages/utilities';
import type { GTMEventGlycerinDisplayForm } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { CmsFormsFieldConfig, CmsFormsFieldOptionsConfig } from '@packages/config/src/default';

import { yupResolver } from '@hookform/resolvers/yup';
import type { FormSubmitResult } from '../types';
import { InputIDs } from '../types';
import { formMessages } from '../messages';
import { ActivityOverlay } from '../ActivityOverlay';
import { SuccessPage } from '../SuccessPage';
import { smallBasicContainer, styleContentTop, stylePanel } from '../styles';
import { getFormattetMessage } from '../helpers/getFormattetMessage';
import {
  frequencyMessages,
  getSuccessMessages,
  pauseMessages,
  unsubscribeMessages,
} from './helpers/messages';
import { FormFooterArea } from '../FormFooterAreaV3';
import type { NewsletterUnsubscribeFormProps } from './types';
import { buildYupSchemaWithIntlFormatMessage } from './helpers/buildYupSchemaWithIntlFormatMessage';
import { getDefaultValues } from './helpers/getDefaultValues';
import { useSubmitForm } from './helpers/useSubmitForm';
import { useGetFieldProps } from './helpers/useGetFieldProps';

/**
 * The Newsletter unsubscribe form.
 *
 * It allows the user to unsubsribe from newsletter, change the frequency of receiving it
 * or set a pause time period.
 *
 * There can be different configurations for each tenant of what can be selected (see readme.md)
 */
export const NewsletterUnsubscribeForm = ({
  headline,
  copyText,
  areaKey,
  buttonLabel,
  buttonLabelMobile,
  token,
}: NewsletterUnsubscribeFormProps) => {
  const { formatMessage } = useIntl();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();

  const [formSubmitResult, setFormSubmitResult] = useState<FormSubmitResult | null>(null);

  const { isMobile } = useDeviceType();
  const customButtonLabel = isMobile ? buttonLabelMobile : buttonLabel;
  const defaultLabel = customButtonLabel || formatMessage(unsubscribeMessages.submit);

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: getDefaultValues(customer, token),
    resolver: yupResolver(buildYupSchemaWithIntlFormatMessage(formatMessage)),
  });
  const { register, handleSubmit, setError, control, setValue, watch } = formMethods;
  const { isValid, isSubmitting, errors } = useFormState({ control });
  const {
    forms: {
      newsletterUnsubscribe: { fields },
    },
  } = useConfig();

  const submitForm = useSubmitForm({
    setFormSubmitResult,
    areaKey,
    setError,
  });

  useEffect(
    () => {
      dispatchGTMEvent<GTMEventGlycerinDisplayForm>({
        event: 'DisplayForm',
        DisplayFormData: {
          category: 'newsletter-unsubscribe',
          label: 'registration',
        },
      });
    },
    // INSPIRE-3537 - only fire once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getFieldProps = useGetFieldProps(register, errors);

  // show Success Page if form has been successfully submitted
  if (formSubmitResult?.successful) {
    const msg = getSuccessMessages(formSubmitResult?.resultType);
    return (
      <SuccessPage
        headline={formatMessage(msg.headline)}
        description={formatMessage(msg.description)}
      />
    );
  }
  return (
    <ActivityOverlay showLoader={isSubmitting}>
      {headline && (
        <Box sx={styleContentTop}>
          <Typography variant="h3" component="div" sx={{ marginBottom: 2.25 }}>
            {headline}
          </Typography>
          {copyText}
        </Box>
      )}
      <FormProvider {...formMethods}>
        <form noValidate onSubmit={handleSubmit(submitForm)} name="unsubscribeForm">
          <Box sx={stylePanel}>
            <Card sx={smallBasicContainer}>
              <CardContent sx={{ padding: 3 }}>
                <Stack spacing={4}>
                  {fields
                    .filter(
                      (item: CmsFormsFieldConfig) =>
                        item.formType === 'radio' && item.options && item.options?.length > 0,
                    )
                    .map((item: CmsFormsFieldConfig) => (
                      <Box key={item.id}>
                        <Box
                          sx={{
                            '& .MuiFormControl-root': {
                              // upper label
                              '&:first-of-type > label': {
                                typography: 'body2',
                                fontWeight: 'fontWeightBold',
                                color: 'text.primary',
                                marginBottom: 1,
                              },
                              // each option
                              '& label:not(:first-child)': {
                                marginTop: 1,
                                // each option label
                                '& span': { typography: 'body2' },
                              },
                            },
                          }}
                        >
                          <RadioGroup
                            initialValue=""
                            label={formatMessage(formMessages[InputIDs.FREQUENCY]?.label)}
                            options={item.options!.map((option: CmsFormsFieldOptionsConfig) => ({
                              label: getFormattetMessage(
                                frequencyMessages,
                                option.messageKey,
                                formatMessage,
                              ),
                              value: String(option.value), // e.g. "unsubscribe|99" or "frequency|0"
                            }))}
                            handleChange={(_e, value) => setValue(InputIDs.FREQUENCY, value)}
                            control={control}
                            controllerName={InputIDs.FREQUENCY}
                          />
                        </Box>
                        {item.additionalForm?.options &&
                          item.additionalForm.options.length > 0 &&
                          // render the subform if the selected value matches "showOnlyWhenWhatSelected"
                          // or if "showOnlyWhenWhatSelected" is not specified
                          (!item.additionalForm.showOnlyWhenWhatSelected ||
                            watch(InputIDs.FREQUENCY)?.includes(
                              item.additionalForm.showOnlyWhenWhatSelected,
                            )) && (
                            <Select
                              label={formatMessage(formMessages[InputIDs.PAUSE]?.label)}
                              items={item.additionalForm.options.map(
                                (option: CmsFormsFieldOptionsConfig) => ({
                                  label: getFormattetMessage(
                                    pauseMessages,
                                    option.messageKey,
                                    formatMessage,
                                  ),
                                  value: String(option.value),
                                }),
                              )}
                              controlProps={{ fullWidth: true }}
                              control={control}
                              controllerName={InputIDs.PAUSE}
                            />
                          )}
                      </Box>
                    ))}

                  <TextField
                    {...getFieldProps(InputIDs.EMAIL)}
                    required
                    placeholder={formatMessage(formMessages[InputIDs.EMAIL].placeholder)}
                    {...register}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <FormFooterArea
            isValid={isValid}
            submitButton={{
              enabled: defaultLabel,
              disabled: formatMessage(unsubscribeMessages.submitDisabled),
            }}
          />
        </form>
      </FormProvider>
    </ActivityOverlay>
  );
};
